import React from "react";
import { DropdownMenu, IconButton, Text } from "@radix-ui/themes";
import { Sparkles } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { WorkflowAction, WorkflowStep } from "@/types/breeze/Workflow";
import { entityService } from "@/services/breeze/EntityService";
import RelationshipFieldTree from "@/components/entity/RelationshipFieldTree";

const DATA_ACTIONS = new Set<WorkflowAction>([
  WorkflowAction.ReadData,
  WorkflowAction.CreateData,
  WorkflowAction.UpdateData,
  WorkflowAction.UpsertData,
]);

function getStepEntity(step: WorkflowStep): string | undefined {
  const p = step.parameters ?? {};
  const name = p.entity ?? p.entity_name;
  return typeof name === "string" && name ? name : undefined;
}

interface VariableMenuProps {
  existingSteps: WorkflowStep[];
  onInsert: (variableText: string) => void;
  showLoopContext?: boolean;
  /** When set, "Trigger Data" expands into a submenu of the entity's actual fields. */
  triggerEntity?: string;
}

export default function VariableMenu({
  existingSteps,
  onInsert,
  showLoopContext = false,
  triggerEntity,
}: VariableMenuProps) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <IconButton type="button" variant="soft" size="2" color="gray" title="Select variables">
          <Sparkles size={13} />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" style={{ minWidth: 220, maxHeight: 350, zIndex: 11000 }}>
        {showLoopContext && (
          <>
            <DropdownMenu.Label>Current Loop Item</DropdownMenu.Label>
            <DropdownMenu.Item onClick={() => onInsert("{{  }}")}>
              Local Item Property...
            </DropdownMenu.Item>
            <DropdownMenu.Separator />
          </>
        )}

        <DropdownMenu.Label>Trigger Properties</DropdownMenu.Label>
        <DropdownMenu.Item onClick={() => onInsert("{{ $anythink.trigger.id }}")}>
          Trigger Job ID
        </DropdownMenu.Item>
        <DropdownMenu.Item onClick={() => onInsert("{{ $anythink.trigger.data }}")}>
          Trigger Data (Entire Object)
        </DropdownMenu.Item>
        {triggerEntity ? (
          <TriggerDataSubmenu entityName={triggerEntity} onInsert={onInsert} />
        ) : (
          <DropdownMenu.Item onClick={() => onInsert("{{ $anythink.trigger.data. }}")}>
            Trigger Data Property...
          </DropdownMenu.Item>
        )}

        {existingSteps.length > 0 && (
          <>
            <DropdownMenu.Separator />
            <DropdownMenu.Label>Preceding Steps Output</DropdownMenu.Label>
            <StepOutputItems steps={existingSteps} onInsert={onInsert} />
          </>
        )}

        <DropdownMenu.Separator />
        <DropdownMenu.Label>Helper Utilities</DropdownMenu.Label>
        <DropdownMenu.Item onClick={() => onInsert("{{ $anythink.now }}")}>
          Current Timestamp (UTC)
        </DropdownMenu.Item>
        <DropdownMenu.Item
          onClick={() => {
            const secretKey = prompt("Enter secret key name (e.g. STRIPE_API_KEY):");
            if (secretKey) {
              onInsert(`{{ $anythink.secrets.${secretKey} }}`);
            }
          }}
        >
          Secret Value...
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export function StepOutputItems({ steps, onInsert }: { steps: WorkflowStep[]; onInsert: (text: string) => void }) {
  return (
    <>
      {steps.map((step) => {
        if (!step.key) return null;
        const entityName = DATA_ACTIONS.has(step.action) ? getStepEntity(step) : undefined;
        const isPush = step.action === WorkflowAction.SendPushNotification;
        return (
          <DropdownMenu.Sub key={step.key}>
            <DropdownMenu.SubTrigger>{step.name || step.key}</DropdownMenu.SubTrigger>
            <DropdownMenu.SubContent style={{ minWidth: 200, maxHeight: 360, zIndex: 11000 }}>
              <DropdownMenu.Item onClick={() => onInsert(`{{ $anythink.steps.${step.key} }}`)}>
                Entire Result Object
              </DropdownMenu.Item>
              <DropdownMenu.Item onClick={() => onInsert(`{{ $anythink.steps.${step.key}.data }}`)}>
                Result Data Array
              </DropdownMenu.Item>
              <DropdownMenu.Item onClick={() => onInsert(`{{ $anythink.steps.${step.key}.data[0] }}`)}>
                First Item in Array
              </DropdownMenu.Item>
              <DropdownMenu.Item onClick={() => onInsert(`{{ $anythink.steps.${step.key}.data[0].id }}`)}>
                First Item ID
              </DropdownMenu.Item>
              {isPush && (
                <>
                  <DropdownMenu.Separator />
                  <DropdownMenu.Label>Push Delivery Summary</DropdownMenu.Label>
                  <DropdownMenu.Item onClick={() => onInsert(`{{ $anythink.steps.${step.key}.data.delivered }}`)}>
                    Delivered Count
                  </DropdownMenu.Item>
                  <DropdownMenu.Item onClick={() => onInsert(`{{ $anythink.steps.${step.key}.data.failed }}`)}>
                    Failed Count
                  </DropdownMenu.Item>
                  <DropdownMenu.Item onClick={() => onInsert(`{{ $anythink.steps.${step.key}.data.unregistered }}`)}>
                    Unregistered Count
                  </DropdownMenu.Item>
                  <DropdownMenu.Item onClick={() => onInsert(`{{ $anythink.steps.${step.key}.data.iterations }}`)}>
                    Iterations (Recipients)
                  </DropdownMenu.Item>
                  <DropdownMenu.Item onClick={() => onInsert(`{{ $anythink.steps.${step.key}.data.send_logs }}`)}>
                    Per-Recipient Send Logs
                  </DropdownMenu.Item>
                </>
              )}
              {entityName && (
                <>
                  <DropdownMenu.Separator />
                  <DropdownMenu.Sub>
                    <DropdownMenu.SubTrigger>For each row (loop)…</DropdownMenu.SubTrigger>
                    <DropdownMenu.SubContent style={{ minWidth: 220, maxHeight: 360, zIndex: 11000 }}>
                      <RelationshipFieldTree
                        entityName={entityName}
                        maxDepth={2}
                        allowRelationshipAsLeaf
                        onSelect={(path) => onInsert(`{{ $anythink.steps.${step.key}.data[*].${path} }}`)}
                      />
                    </DropdownMenu.SubContent>
                  </DropdownMenu.Sub>
                </>
              )}
              <DropdownMenu.Separator />
              <DropdownMenu.Item onClick={() => onInsert(`{{ $anythink.steps.${step.key}. }}`)}>
                Custom Property Path...
              </DropdownMenu.Item>
            </DropdownMenu.SubContent>
          </DropdownMenu.Sub>
        );
      })}
    </>
  );
}

export interface TriggerDataSubmenuProps {
  entityName: string;
  onInsert: (text: string) => void;
}

export function TriggerDataSubmenu({ entityName, onInsert }: TriggerDataSubmenuProps) {
  const { data: entity } = useQuery({
    queryKey: ["entity", entityName, true],
    queryFn: () => entityService.getByName(entityName, true),
    staleTime: 5 * 60 * 1000,
  });

  if (!entity) {
    return (
      <DropdownMenu.Item onClick={() => onInsert("{{ $anythink.trigger.data. }}")}>
        Trigger Data Property...
      </DropdownMenu.Item>
    );
  }

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger>
        Trigger Data: {entity.name}
      </DropdownMenu.SubTrigger>
      <DropdownMenu.SubContent style={{ minWidth: 240, maxHeight: 360, zIndex: 11000 }}>
        <DropdownMenu.Item onClick={() => onInsert("{{ $anythink.trigger.data. }}")}>
          <Text size="1" color="gray">Custom property…</Text>
        </DropdownMenu.Item>
        <DropdownMenu.Separator />
        <RelationshipFieldTree
          entityName={entity.name}
          maxDepth={2}
          allowRelationshipAsLeaf
          onSelect={(path) => onInsert(`{{ $anythink.trigger.data.${path} }}`)}
        />
      </DropdownMenu.SubContent>
    </DropdownMenu.Sub>
  );
}
